// Drift: medarbejderens oplysninger til løn og ID-dokumenter, og mersalg (del af SupabaseApi – se supabase.ts).
// CPR og konto gemmes krypteret og ses kun maskeret – det fulde nummer kun af ejer/admin, én ad gangen, og det logges
// (20261025120000_medarbejderdata.sql). ID-billederne ligger i den private bucket "medarbejder-dokumenter" (<klient>/<profil>/<fil>).
// Mersalg regnes her med samme kode som testversionen (core/mersalg.ts) ud fra mersalg_data (20261029200000_kontor3.sql).
import { ApiFejl, type Api } from './api';
import type { Booking, Profil, Tilvalg } from '../../core/typer';
import { DOK_TYPER, tjekFil, validerKoerekort, validerLoen, type DokumentVisning, type KoerekortVisning, type LoenInput, type LoenVisning } from '../../core/medarbejderdata';
import type { TeamOpgave } from '../../core/invitation';
import { iDag } from '../../core/dato';
import { beregnMersalg } from '../../core/mersalg';
import { gyldigDato, rens } from '../../core/validering';
import type { SbKontekst } from './sbKatalog';

const BUCKET = 'medarbejder-dokumenter';
const fejl = (b: string, f: Record<string, string> = {}) => new ApiFejl(b, f);
const KOL = 'id, profil_id, type, filnavn, mime, uploadet, af';
const EXT: Record<string, string> = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'image/heic': 'heic', 'application/pdf': 'pdf' };

export class SbMedarbejder {
  constructor(private readonly c: SbKontekst) {}
  private async rpc<T>(navn: string, args: Record<string, unknown> = {}): Promise<T> {
    return (await this.c.k(this.c.sb.rpc(navn, { k: await this.c.kraevKlient(), ...args }))) as T;
  }
  private async minProfil(): Promise<string> {
    const p = await this.rpc<string | null>('min_profil');
    if (!p) throw fejl('Kun for medarbejdere.');
    return p;
  }
  private async loen(p: string): Promise<LoenVisning | null> {
    const r = (await this.rpc<LoenVisning[] | null>('medarbejder_loen_vis', { p })) ?? [];
    return r[0] ?? null;
  }
  private async dokumenter(p: string): Promise<DokumentVisning[]> {
    return (await this.c.k(this.c.sb.from('medarbejder_dokumenter').select(KOL).eq('klient_id', await this.c.kraevKlient()).eq('profil_id', p).order('uploadet', { ascending: false }))) as DokumentVisning[];
  }
  private async koerekort(p: string): Promise<KoerekortVisning | null> {
    return (await this.c.k(this.c.sb.from('medarbejder_koerekort').select('gyldig_til, opdateret').eq('klient_id', await this.c.kraevKlient()).eq('profil_id', p).maybeSingle())) as KoerekortVisning | null;
  }
  private static tilRpc(l: LoenInput) { return { p_navn: l.fulde_navn, p_cpr: l.cpr, p_reg: l.reg, p_konto: l.konto, p_straf: l.straffeattest_ren }; }

  // ---------- Steameren selv ----------
  mineDokumenter: Api['mineDokumenter'] = async () => {
    const p = await this.minProfil();
    const [loen, dokumenter, koerekort] = await Promise.all([this.loen(p), this.dokumenter(p), this.koerekort(p)]);
    return { loen, dokumenter, koerekort };
  };
  gemMineLoenoplysninger: Api['gemMineLoenoplysninger'] = async (v) => {
    const l = validerLoen(v); if (!l.ok) throw fejl('Tjek de markerede felter.', l.fejl);
    await this.rpc('gem_mine_loenoplysninger', SbMedarbejder.tilRpc(l.data));
    this.c.skift();
  };
  uploadMitDokument: Api['uploadMitDokument'] = async (d) => {
    if (!DOK_TYPER.includes(d.type)) throw fejl('Vælg hvilket ID du uploader.', { dokument: 'Vælg type.' });
    const tf = tjekFil(d.data); if ('fejl' in tf) throw fejl(tf.fejl, { dokument: tf.fejl });
    const [k, p] = [await this.c.kraevKlient(), await this.minProfil()];
    const navn = (await this.c.k(this.c.sb.from('profiler').select('navn').eq('id', p).single()) as { navn: string }).navn;
    const sti = `${k}/${p}/${d.type}-${crypto.randomUUID()}.${EXT[tf.mime]}`;
    const { error } = await this.c.sb.storage.from(BUCKET).upload(sti, await (await fetch(d.data)).blob(), { contentType: tf.mime, upsert: false });
    if (error) throw fejl('Filen kunne ikke gemmes. Tjek forbindelsen, og prøv igen.');
    await this.c.k(this.c.sb.from('medarbejder_dokumenter').insert({ klient_id: k, profil_id: p, type: d.type, sti, filnavn: rens(d.filnavn, 120) || 'id', mime: tf.mime, af: navn }));
    this.c.skift();
  };
  gemMitKoerekort: Api['gemMitKoerekort'] = async (v) => {
    const har = await this.dokumenter(await this.minProfil());
    const f = validerKoerekort(null, v, iDag(), { forside: har.some((x) => x.type === 'koerekort'), bagside: har.some((x) => x.type === 'koerekort_bag') });
    if (Object.keys(f).length) throw fejl(Object.values(f)[0], f);
    if (v.forside) await this.uploadMitDokument({ ...v.forside, type: 'koerekort' });
    if (v.bagside) await this.uploadMitDokument({ ...v.bagside, type: 'koerekort_bag' });
    await this.rpc('gem_mit_koerekort', { p_gyldig_til: v.gyldig_til });   // tjekker igen: begge sider og datoen
    this.c.skift();
  };
  /** Åbn et dokument: steameren sine egne, ejer/admin alle (og det logges). */
  hentDokument: Api['hentDokument'] = async (dok_id) => {
    const k = await this.c.kraevKlient();
    const d = (await this.c.k(this.c.sb.from('medarbejder_dokumenter').select('id, profil_id, sti, filnavn, mime').eq('klient_id', k).eq('id', dok_id).maybeSingle())) as
      { id: string; profil_id: string; sti: string; filnavn: string; mime: string } | null;
    if (!d) throw fejl('Dokumentet findes ikke.');
    if (d.profil_id !== (await this.rpc<string | null>('min_profil'))) await this.rpc('log_dokument_visning', { d: d.id });
    const { data, error } = await this.c.sb.storage.from(BUCKET).download(d.sti);
    if (error || !data) throw fejl('Dokumentet kunne ikke hentes. Prøv igen.');
    const url = await new Promise<string>((ok, nej) => { const r = new FileReader(); r.onload = () => ok(String(r.result)); r.onerror = () => nej(r.error); r.readAsDataURL(data); });
    return { data: url.replace(/^data:[^;]*;/, `data:${d.mime};`), mime: d.mime, filnavn: d.filnavn };
  };

  // ---------- Ejer og admin ----------
  medarbejderData: Api['medarbejderData'] = async (profil_id) => {
    const p = (await this.c.k(this.c.sb.from('profiler').select('navn, rolle').eq('klient_id', await this.c.kraevKlient()).eq('id', profil_id).maybeSingle())) as Pick<Profil, 'navn' | 'rolle'> | null;
    if (!p || p.rolle !== 'steamer') throw fejl('Steameren findes ikke.');
    const [loen, dokumenter, koerekort] = await Promise.all([this.loen(profil_id), this.dokumenter(profil_id), this.koerekort(profil_id)]);
    return { navn: p.navn, loen, dokumenter, koerekort };
  };
  visFoelsomt: Api['visFoelsomt'] = (profil_id, felt) => this.rpc<string>('vis_foelsomt', { p: profil_id, felt });
  retMedarbejderLoen: Api['retMedarbejderLoen'] = async (profil_id, v) => {
    const l = validerLoen(v); if (!l.ok) throw fejl('Tjek de markerede felter.', l.fejl);
    await this.rpc('ret_medarbejder_loen', { p: profil_id, ...SbMedarbejder.tilRpc(l.data) });
    this.c.skift();
  };
  sletMedarbejderDokument: Api['sletMedarbejderDokument'] = async (dok_id) => {
    const k = await this.c.kraevKlient();
    const d = (await this.c.k(this.c.sb.from('medarbejder_dokumenter').delete().eq('klient_id', k).eq('id', dok_id).select('sti'))) as { sti: string }[];
    if (!d.length) throw fejl('Dokumentet findes ikke.');
    await this.c.sb.storage.from(BUCKET).remove(d.map((x) => x.sti));
    this.c.skift();
  };
  teamOpgaver: Api['teamOpgaver'] = async () => ((await this.rpc<TeamOpgave[] | null>('team_opgaver')) ?? []);
  bedOmDokumenter: Api['bedOmDokumenter'] = async (profil_id) => { await this.rpc('bed_om_dokumenter', { p_profil: profil_id }); this.c.skift(); };

  // ---------- Mersalg ----------
  mersalg: Api['mersalg'] = async (fra, til) => {
    if (!gyldigDato(fra) || !gyldigDato(til) || fra > til) throw fejl('Vælg en gyldig periode.');
    const d = await this.rpc<{ kun: string | null; profiler: Pick<Profil, 'id' | 'navn' | 'rolle' | 'steamer_id'>[]; tilvalg: Pick<Tilvalg, 'id' | 'navn'>[];
      bookinger: (Pick<Booking, 'id' | 'dato' | 'status' | 'steamer_id' | 'ekstra'> & { kunde: string | null })[] }>('mersalg_data', { p_fra: fra, p_til: til });
    const mig = d.kun ? d.profiler.find((p) => p.id === d.kun) : null;
    const r = beregnMersalg({ bookinger: d.bookinger as unknown as Booking[], profiler: d.profiler, tilvalg: d.tilvalg, fra, til, kun: d.kun });
    if (mig && !r.raekker.length) r.raekker.push({ id: mig.id, navn: mig.navn, rolle: 'steamer', opgaver: 0, tilbudt: 0, godkendt: 0, afvist: 0, afventer: 0, beloeb: 0, total: 0, aendretNed: 0, aendretNedBeloeb: 0, rate: null, prOpgave: null, andel: null, top: [], linjer: [] });
    const kunder: Record<number, string> = {};
    for (const b of d.bookinger) kunder[b.id] = b.kunde ?? '';
    return { ...r, kunder };
  };
}
